import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS,
  SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS,
  createSpecialistLocationGatewayIntentRecord,
  isSpecialistLocationGatewayInitialSourceActionIntentPair,
  isSpecialistLocationGatewayIdentifier,
  isSpecialistLocationGatewayRecordFresh,
  validateSpecialistLocationGatewayIntentRecord,
  validateSpecialistLocationGatewayPublication,
  type SpecialistLocationGatewayIntentRecordV1,
  type SpecialistLocationGatewayIntentV1,
  type SpecialistLocationGatewaySourceAction,
  type SpecialistLocationGatewaySourceV1,
  type SpecialistLocationGatewayPublicationV1,
} from "./contract";
import { isSpecialistLocationGatewayOpaquePointer } from "./handoff";

export const SPECIALIST_LOCATION_GATEWAY_INTENT_STORAGE_KEY =
  "volera:specialist-location-gateway:intents:v1";
export const SPECIALIST_LOCATION_GATEWAY_INTENT_MAX_ENTRIES = 16;

const intentStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayIntentRecordV1>({
    storageKey: SPECIALIST_LOCATION_GATEWAY_INTENT_STORAGE_KEY,
    storeKind: "specialist-location-gateway-intents",
    maxEntries: SPECIALIST_LOCATION_GATEWAY_INTENT_MAX_ENTRIES,
    maxAgeMs: 24 * 60 * 60 * 1_000,
    maxSerializedCharacters: 120_000,
    validate: validateSpecialistLocationGatewayIntentRecord,
  });

function safeIntentEpoch(value: number | undefined) {
  try {
    const epoch = value ?? Date.now();
    return Number.isSafeInteger(epoch) && epoch >= 0 ? epoch : null;
  } catch {
    return null;
  }
}

function inspectIntentStrict(draftId: string) {
  try {
    return intentStore.inspectPersistedDraft(draftId);
  } catch {
    return { status: "unreadable" } as const;
  }
}

/**
 * Internal lifecycle proof for a committed destination. Unlike the public
 * pre-draft reader, this deliberately does not apply the short intent TTL:
 * the caller must already hold the exact committed destination/lineage proof
 * and uses this only to prove that the persisted source record is byte-exact.
 */
export function inspectSpecialistLocationGatewayIntentExactPersisted(
  expected: unknown,
) {
  let validation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    validation = validateSpecialistLocationGatewayIntentRecord(expected);
  } catch {
    return { status: "invalid" } as const;
  }
  if (!validation.valid) return { status: "invalid" } as const;
  const inspected = inspectIntentStrict(validation.draft.draftId);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  if (inspected.status === "absent") return { status: "absent" } as const;
  return JSON.stringify(inspected.draft) === JSON.stringify(validation.draft)
    ? { status: "present-exact", intent: inspected.draft } as const
    : { status: "conflict" } as const;
}

export function createSpecialistLocationGatewayOpaquePointer() {
  try {
    if (typeof crypto === "undefined") return null;

    if (typeof crypto.randomUUID === "function") {
      const pointer = `slg1-${crypto.randomUUID()}`;
      return isSpecialistLocationGatewayOpaquePointer(pointer)
        ? pointer
        : null;
    }

    if (typeof crypto.getRandomValues !== "function") return null;
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const token = Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("");
    const pointer = `slg1-${token}`;
    return isSpecialistLocationGatewayOpaquePointer(pointer) ? pointer : null;
  } catch {
    return null;
  }
}

export interface CreateAndStoreSpecialistLocationGatewayIntentInput {
  handoffId?: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  nowEpochMs?: number;
  ttlMs?: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
}

function createAndStoreSpecialistLocationGatewayIntentInternal(
  input: CreateAndStoreSpecialistLocationGatewayIntentInput,
) {
  const nowEpochMs = safeIntentEpoch(input.nowEpochMs);
  const ttlMs = input.ttlMs ?? SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS;
  const expiresAtEpochMs =
    nowEpochMs === null ||
    !Number.isSafeInteger(ttlMs) ||
    ttlMs <= 0 ||
    ttlMs > SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS ||
    !Number.isSafeInteger(nowEpochMs + ttlMs)
      ? null
      : nowEpochMs + ttlMs;
  let handoffId: string | null;
  try {
    handoffId =
      input.handoffId ?? createSpecialistLocationGatewayOpaquePointer();
  } catch {
    handoffId = null;
  }
  if (
    !handoffId ||
    !isSpecialistLocationGatewayOpaquePointer(handoffId) ||
    nowEpochMs === null ||
    expiresAtEpochMs === null ||
    !Number.isSafeInteger(input.generation) ||
    input.generation < 0 ||
    !isSpecialistLocationGatewayInitialSourceActionIntentPair(
      input.sourceAction,
      input.intent,
    )
  ) {
    return { status: "invalid" } as const;
  }

  let validation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    const record = createSpecialistLocationGatewayIntentRecord({
      draftId: handoffId,
      gatewayProviderId: input.gatewayProviderId,
      gatewayProviderRevision: input.gatewayProviderRevision,
      customerAccountId: input.customerAccountId,
      locale: input.locale,
      generation: input.generation,
      createdAtEpochMs: nowEpochMs,
      expiresAtEpochMs,
      sourceAction: input.sourceAction,
      source: input.source,
      intent: input.intent,
    });
    validation = validateSpecialistLocationGatewayIntentRecord(record);
  } catch {
    return { status: "invalid" } as const;
  }
  if (!validation.valid) return { status: "invalid" } as const;

  let stored: ReturnType<typeof intentStore.createDraftStrictPersisted>;
  try {
    stored = intentStore.createDraftStrictPersisted(validation.draft);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  if (
    stored.status === "unreadable" ||
    stored.status === "persistence-failed" ||
    stored.status === "readback-mismatch"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  return stored.status === "created"
    ? { status: "created", intent: stored.draft } as const
    : stored;
}

/** Public constructor boundary: malformed runtime inputs never escape. */
export function createAndStoreSpecialistLocationGatewayIntent(
  input: CreateAndStoreSpecialistLocationGatewayIntentInput,
) {
  try {
    return createAndStoreSpecialistLocationGatewayIntentInternal(input);
  } catch {
    // Every persistence call inside the internal operation has its own
    // absorbing catch. Reaching this boundary therefore proves pre-write
    // deterministic invalid input/clock/entropy/constructor failure.
    return { status: "invalid" } as const;
  }
}

export type ReadSpecialistLocationGatewayIntentResult =
  | { status: "ready"; intent: SpecialistLocationGatewayIntentRecordV1 }
  | {
      status: "unavailable";
      reason:
        | "customer-owner-mismatch"
        | "expired"
        | "locale-mismatch"
        | "missing"
        | "source-route-mismatch"
        | "unreadable";
    };

export interface ReadSpecialistLocationGatewayIntentInput {
  handoffId: string;
  nowEpochMs?: number;
  expectedCustomerAccountId: string | null;
  expectedLocale: string;
  expectedSourceLocationId: string;
  expectedSourceUserLocationId: string;
}

function readSpecialistLocationGatewayIntentStrictInternal(
  input: ReadSpecialistLocationGatewayIntentInput,
): ReadSpecialistLocationGatewayIntentResult {
  if (!isSpecialistLocationGatewayOpaquePointer(input.handoffId)) {
    return { status: "unavailable", reason: "unreadable" };
  }
  const inspected = inspectIntentStrict(input.handoffId);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
  if (inspected.status === "absent") {
    return { status: "unavailable", reason: "missing" };
  }
  const intent = inspected.draft;
  const nowEpochMs = safeIntentEpoch(input.nowEpochMs);
  if (
    nowEpochMs === null ||
    !isSpecialistLocationGatewayRecordFresh(intent, nowEpochMs)
  ) {
    return { status: "unavailable", reason: "expired" };
  }
  if (
    intent.customerAccountId !== input.expectedCustomerAccountId
  ) {
    return { status: "unavailable", reason: "customer-owner-mismatch" };
  }
  if (
    intent.source.sourceLocationId !== input.expectedSourceLocationId ||
    intent.source.sourceUserLocationId !== input.expectedSourceUserLocationId
  ) {
    return { status: "unavailable", reason: "source-route-mismatch" };
  }
  if (intent.locale !== input.expectedLocale) {
    return { status: "unavailable", reason: "locale-mismatch" };
  }
  return { status: "ready", intent };
}

export function readSpecialistLocationGatewayIntentStrict(
  input: ReadSpecialistLocationGatewayIntentInput,
): ReadSpecialistLocationGatewayIntentResult {
  try {
    return readSpecialistLocationGatewayIntentStrictInternal(input);
  } catch {
    return { status: "unavailable", reason: "unreadable" };
  }
}

/**
 * TTL-independent pre-draft lifecycle proof for an explicit Close action.
 * It exact-binds the opaque pointer, customer, source route and locale but
 * never authorizes another mutable provider action.
 */
export function readSpecialistLocationGatewayIntentForLifecycleStrict(
  input: Omit<ReadSpecialistLocationGatewayIntentInput, "nowEpochMs">,
): ReadSpecialistLocationGatewayIntentResult {
  try {
    if (!isSpecialistLocationGatewayOpaquePointer(input.handoffId)) {
      return { status: "unavailable", reason: "unreadable" };
    }
    const inspected = inspectIntentStrict(input.handoffId);
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unavailable", reason: "unreadable" };
    }
    if (inspected.status === "absent") {
      return { status: "unavailable", reason: "missing" };
    }
    const intent = inspected.draft;
    if (intent.customerAccountId !== input.expectedCustomerAccountId) {
      return {
        status: "unavailable",
        reason: "customer-owner-mismatch",
      };
    }
    if (
      intent.source.sourceLocationId !== input.expectedSourceLocationId ||
      intent.source.sourceUserLocationId !==
        input.expectedSourceUserLocationId
    ) {
      return { status: "unavailable", reason: "source-route-mismatch" };
    }
    if (intent.locale !== input.expectedLocale) {
      return { status: "unavailable", reason: "locale-mismatch" };
    }
    return { status: "ready", intent };
  } catch {
    return { status: "unavailable", reason: "unreadable" };
  }
}

export type BindSpecialistLocationGatewayIntentAfterAuthResult =
  | { status: "bound"; intent: SpecialistLocationGatewayIntentRecordV1 }
  | {
      status: "failed";
      reason:
        | "already-owned"
        | "invalid-account"
        | "invalid-publication"
        | "owner-drift";
    }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

/**
 * Exact intent-only anonymous -> account CAS for post-draft companion-lineage
 * repair. It is not a safe pre-draft composition: clients with a live
 * resolution must use bindSpecialistLocationGatewayPreDraftAfterAuthStrict,
 * whose persisted claim owns both stores.
 */
export interface BindSpecialistLocationGatewayIntentAfterAuthInput {
  anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
  authenticatedCustomerAccountId: string;
  publication: SpecialistLocationGatewayPublicationV1;
  nowEpochMs: number;
}

function bindSpecialistLocationGatewayIntentAfterAuthStrictInternal(
  input: BindSpecialistLocationGatewayIntentAfterAuthInput,
): BindSpecialistLocationGatewayIntentAfterAuthResult {
  const nowEpochMs = safeIntentEpoch(input.nowEpochMs);
  let anonymousValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    anonymousValidation = validateSpecialistLocationGatewayIntentRecord(
      input.anonymousIntent,
    );
  } catch {
    return { status: "failed", reason: "invalid-publication" };
  }
  if (
    nowEpochMs === null ||
    !anonymousValidation.valid
  ) {
    return { status: "failed", reason: "invalid-publication" };
  }
  const anonymousIntent = anonymousValidation.draft;
  if (anonymousIntent.generation >= Number.MAX_SAFE_INTEGER) {
    return { status: "failed", reason: "invalid-publication" };
  }
  if (anonymousIntent.customerAccountId !== null) {
    return { status: "failed", reason: "already-owned" };
  }
  if (
    !isSpecialistLocationGatewayIdentifier(
      input.authenticatedCustomerAccountId,
    )
  ) {
    return {
      status: "failed",
      reason: "invalid-account",
    };
  }
  let publication: ReturnType<
    typeof validateSpecialistLocationGatewayPublication
  >;
  try {
    publication = validateSpecialistLocationGatewayPublication(
      input.publication,
      {
        intentRecord: anonymousIntent,
        nowEpochMs,
        expectedProviderId: anonymousIntent.gatewayProviderId,
        expectedProviderRevision:
          anonymousIntent.gatewayProviderRevision,
      },
    );
  } catch {
    return { status: "failed", reason: "invalid-publication" };
  }
  if (!publication.valid) {
    return { status: "failed", reason: "invalid-publication" };
  }
  const expiresAtEpochMs = Math.min(
    anonymousIntent.expiresAtEpochMs,
    publication.publication.expiresAtEpochMs,
  );
  if (
    !Number.isSafeInteger(expiresAtEpochMs) ||
    expiresAtEpochMs <= nowEpochMs ||
    expiresAtEpochMs - nowEpochMs >
      SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS
  ) {
    return { status: "failed", reason: "invalid-publication" };
  }
  let nextValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    const next = createSpecialistLocationGatewayIntentRecord({
      draftId: anonymousIntent.draftId,
      gatewayProviderId: anonymousIntent.gatewayProviderId,
      gatewayProviderRevision: anonymousIntent.gatewayProviderRevision,
      customerAccountId: input.authenticatedCustomerAccountId,
      locale: anonymousIntent.locale,
      generation: anonymousIntent.generation + 1,
      createdAtEpochMs: nowEpochMs,
      expiresAtEpochMs,
      sourceAction: anonymousIntent.sourceAction,
      source: anonymousIntent.source,
      intent: anonymousIntent.intent,
    });
    nextValidation = validateSpecialistLocationGatewayIntentRecord(next);
  } catch {
    return { status: "failed", reason: "invalid-publication" };
  }
  if (!nextValidation.valid) {
    return { status: "failed", reason: "invalid-publication" };
  }
  let replaced: ReturnType<typeof intentStore.replaceDraftStrictPersisted>;
  try {
    replaced = intentStore.replaceDraftStrictPersisted(
      anonymousIntent,
      nextValidation.draft,
    );
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (replaced.status === "replaced") {
    return { status: "bound", intent: replaced.draft };
  }
  if (
    replaced.status === "unreadable" ||
    replaced.status === "persistence-failed" ||
    replaced.status === "readback-mismatch"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  return { status: "failed", reason: "owner-drift" };
}

export function bindSpecialistLocationGatewayIntentAfterAuthStrict(
  input: BindSpecialistLocationGatewayIntentAfterAuthInput,
): BindSpecialistLocationGatewayIntentAfterAuthResult {
  try {
    return bindSpecialistLocationGatewayIntentAfterAuthStrictInternal(input);
  } catch {
    // Persistence is contained by the internal exact-CAS catch; any remaining
    // exception is deterministic input/constructor/publication invalidity.
    return { status: "failed", reason: "invalid-publication" };
  }
}

export function removeSpecialistLocationGatewayIntentStrict(
  expected: SpecialistLocationGatewayIntentRecordV1,
) {
  let removed: ReturnType<typeof intentStore.removeDraftStrictPersisted>;
  try {
    removed = intentStore.removeDraftStrictPersisted(expected);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  if (
    removed.status === "unreadable" ||
    removed.status === "persistence-failed" ||
    removed.status === "readback-mismatch"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  return removed;
}

/**
 * Saga-only exact restoration for a record removed by the same reversible
 * customer-owned transaction. It never replaces an existing pointer and is
 * deliberately omitted from the public barrel.
 */
export function restoreSpecialistLocationGatewayIntentStrict(
  expected: SpecialistLocationGatewayIntentRecordV1,
) {
  let validation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    validation = validateSpecialistLocationGatewayIntentRecord(expected);
  } catch {
    return { status: "invalid" } as const;
  }
  if (!validation.valid) return { status: "invalid" } as const;
  let restored: ReturnType<typeof intentStore.createDraftStrictPersisted>;
  try {
    restored = intentStore.createDraftStrictPersisted(validation.draft);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  if (
    restored.status === "unreadable" ||
    restored.status === "persistence-failed" ||
    restored.status === "readback-mismatch"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  return restored.status === "created"
    ? { status: "restored", intent: restored.draft } as const
    : restored;
}

/**
 * Saga-only exact CAS used by SLG authority transitions. Both values must be
 * fully validated records for the same opaque pointer. Ambiguous persistence
 * is absorbing and therefore quarantines every customer-owned mutation lane.
 */
export function compareAndSwapSpecialistLocationGatewayIntentStrict(input: {
  expected: SpecialistLocationGatewayIntentRecordV1;
  next: SpecialistLocationGatewayIntentRecordV1;
}) {
  let expectedValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  let nextValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    expectedValidation = validateSpecialistLocationGatewayIntentRecord(
      input.expected,
    );
    nextValidation = validateSpecialistLocationGatewayIntentRecord(input.next);
  } catch {
    return { status: "invalid" } as const;
  }
  if (
    !expectedValidation.valid ||
    !nextValidation.valid ||
    expectedValidation.draft.draftId !== nextValidation.draft.draftId
  ) {
    return { status: "invalid" } as const;
  }
  let replaced: ReturnType<typeof intentStore.replaceDraftStrictPersisted>;
  try {
    replaced = intentStore.replaceDraftStrictPersisted(
      expectedValidation.draft,
      nextValidation.draft,
    );
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  if (
    replaced.status === "unreadable" ||
    replaced.status === "persistence-failed" ||
    replaced.status === "readback-mismatch"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    } as const;
  }
  return replaced.status === "replaced"
    ? { status: "replaced", intent: replaced.draft } as const
    : replaced;
}

export function inspectSpecialistLocationGatewayIntentsStrict() {
  let inspected: ReturnType<typeof intentStore.inspectPersistedDrafts>;
  try {
    inspected = intentStore.inspectPersistedDrafts();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable", drafts: [] } as const;
  }
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
  }
  return inspected;
}

export function clearSpecialistLocationGatewayIntents() {
  try {
    return intentStore.clearDrafts();
  } catch {
    return false;
  }
}
